import { shadcn } from '@clerk/themes';
import { deDE } from '@clerk/localizations/de-DE';
import { enUS } from '@clerk/localizations/en-US';
import { esES } from '@clerk/localizations/es-ES';
import { frFR } from '@clerk/localizations/fr-FR';
import { CEDRUM_BRAND_LOGOS } from '@cedrum/shared-web/brand/logos';

type ClerkMode = 'light' | 'dark';
type ClerkBrandVariant = 'auto' | 'lockup' | 'mark';
type ClerkSurface = 'embedded' | 'page' | 'modal' | 'popover';

type ClerkAppearanceOptions = {
  mode?: ClerkMode;
  brandVariant?: ClerkBrandVariant;
  surface?: ClerkSurface;
  logoLinkUrl?: string;
  termsPageUrl?: string;
  privacyPageUrl?: string;
  helpPageUrl?: string;
};

const THEME_FALLBACKS = Object.freeze({
  light: Object.freeze({
    surface1: '#ffffff',
    surface2: '#f8fbff',
    text: '#111827',
    textMuted: '#4b5563',
    border: 'rgba(17, 24, 39, 0.12)',
    borderStrong: 'rgba(17, 24, 39, 0.2)',
    accent: '#1c5eff',
    accentContrast: '#ffffff',
    ring: 'rgba(28, 94, 255, 0.28)',
    shadowColor: 'rgba(15, 23, 42, 0.16)',
    modalBackdrop: 'rgba(14, 20, 32, 0.56)',
    danger: '#ef4444',
    success: '#1f8b54',
    warning: '#f36b16',
  }),
  dark: Object.freeze({
    surface1: '#111a29',
    surface2: '#152136',
    text: '#f4f8ff',
    textMuted: '#b9c8e2',
    border: 'rgba(203, 218, 244, 0.16)',
    borderStrong: 'rgba(203, 218, 244, 0.26)',
    accent: '#8cb0ff',
    accentContrast: '#0b111b',
    ring: 'rgba(140, 176, 255, 0.34)',
    shadowColor: 'rgba(0, 0, 0, 0.5)',
    modalBackdrop: 'rgba(2, 6, 13, 0.72)',
    danger: '#ff6f61',
    success: '#52c889',
    warning: '#ffb74d',
  }),
});

const BASE_ELEMENTS = Object.freeze({
  cardBox: 'cedrum-clerk-cardBox',
  card: 'cedrum-clerk-card',
  main: 'cedrum-clerk-main',
  logoBox: 'cedrum-clerk-logoBox',
  logoImage: 'cedrum-clerk-logoImage',
  header: 'cedrum-clerk-header',
  headerTitle: 'cedrum-clerk-headerTitle',
  headerSubtitle: 'cedrum-clerk-headerSubtitle',
  formHeader: 'cedrum-clerk-formHeader',
  formHeaderTitle: 'cedrum-clerk-formHeaderTitle',
  formHeaderSubtitle: 'cedrum-clerk-formHeaderSubtitle',
  formHeaderBackLink: 'cedrum-clerk-backLink',
  backLink: 'cedrum-clerk-backLink',
  form: 'cedrum-clerk-form',
  formField: 'cedrum-clerk-formField',
  formFieldLabel: 'cedrum-clerk-formLabel',
  formFieldHintText: 'cedrum-clerk-formHint',
  formFieldErrorText: 'cedrum-clerk-formError',
  formFieldWarningText: 'cedrum-clerk-formWarning',
  formFieldSuccessText: 'cedrum-clerk-formSuccess',
  formFieldAction: 'cedrum-clerk-formFieldAction',
  formFieldInput: 'cedrum-clerk-input',
  formFieldInputShowPasswordButton: 'cedrum-clerk-inputAction',
  formFieldInputShowPasswordIcon: 'cedrum-clerk-inputActionIcon',
  formButtonPrimary: 'cedrum-clerk-buttonPrimary',
  formButtonPrimary__loading: 'cedrum-clerk-buttonPrimary',
  formButtonReset: 'cedrum-clerk-buttonSecondary',
  formResendCodeLink: 'cedrum-clerk-formResendCodeLink',
  otpCodeFieldInput: 'cedrum-clerk-otpCodeFieldInput',
  otpCodeFieldErrorText: 'cedrum-clerk-otpCodeFieldErrorText',
  otpCodeFieldSuccessText: 'cedrum-clerk-otpCodeFieldSuccessText',
  dividerRow: 'cedrum-clerk-dividerRow',
  dividerLine: 'cedrum-clerk-dividerLine',
  dividerText: 'cedrum-clerk-dividerText',
  socialButtonsRoot: 'cedrum-clerk-socialRoot',
  socialButtons: 'cedrum-clerk-socialButtons',
  socialButtonsBlockButton: 'cedrum-clerk-socialButton',
  socialButtonsProviderIcon: 'cedrum-clerk-socialProviderIcon',
  footer: 'cedrum-clerk-footer',
  footerAction: 'cedrum-clerk-footerAction',
  footerActionText: 'cedrum-clerk-footerActionText',
  footerActionLink: 'cedrum-clerk-footerActionLink',
  footerPages: 'cedrum-clerk-footerPages',
  footerPagesLink: 'cedrum-clerk-footerPagesLink',
  identityPreview: 'cedrum-clerk-identityPreview',
  identityPreviewText: 'cedrum-clerk-identityPreviewText',
  identityPreviewEditButton: 'cedrum-clerk-identityPreviewEditButton',
  identityPreviewEditButtonIcon: 'cedrum-clerk-identityPreviewEditButtonIcon',
  alert: 'cedrum-clerk-alert',
  alertText: 'cedrum-clerk-alertText',
  alertIcon: 'cedrum-clerk-alertIcon',
  badge: 'cedrum-clerk-badge',
  avatarBox: 'cedrum-clerk-avatarBox',
  modalBackdrop: 'cedrum-clerk-modalBackdrop',
  modalContent: 'cedrum-clerk-modalContent',
  modalCloseButton: 'cedrum-clerk-modalCloseButton',
  userButtonBox: 'cedrum-clerk-userButtonBox',
  userButtonTrigger: 'cedrum-clerk-userButtonTrigger',
  userButtonAvatarBox: 'cedrum-clerk-userButtonAvatarBox',
  userButtonPopoverCard: 'cedrum-clerk-userButtonPopoverCard',
  userButtonPopoverMain: 'cedrum-clerk-userButtonPopoverMain',
  userButtonPopoverActions: 'cedrum-clerk-userButtonPopoverActions',
  userButtonPopoverActionButton: 'cedrum-clerk-userButtonPopoverActionButton',
  userButtonPopoverFooter: 'cedrum-clerk-userButtonPopoverFooter',
  userButtonPopoverFooterPagesLink: 'cedrum-clerk-userButtonPopoverFooterLink',
  userPreviewMainIdentifier: 'cedrum-clerk-userPreviewMainIdentifier',
  userPreviewSecondaryIdentifier: 'cedrum-clerk-userPreviewSecondaryIdentifier',
  organizationSwitcherTrigger: 'cedrum-clerk-orgSwitcherTrigger',
  organizationSwitcherTriggerIcon: 'cedrum-clerk-orgSwitcherTriggerIcon',
  organizationSwitcherPopoverCard: 'cedrum-clerk-orgSwitcherPopoverCard',
  organizationSwitcherPopoverMain: 'cedrum-clerk-orgSwitcherPopoverMain',
  organizationSwitcherPopoverActionButton: 'cedrum-clerk-orgSwitcherActionButton',
  organizationSwitcherPopoverFooter: 'cedrum-clerk-orgSwitcherPopoverFooter',
  organizationSwitcherPopoverFooterPagesLink: 'cedrum-clerk-orgSwitcherPopoverFooterLink',
  organizationPreviewTextContainer: 'cedrum-clerk-orgPreviewText',
  organizationListItem: 'cedrum-clerk-orgListItem',
  page: 'cedrum-clerk-profilePage',
  navbar: 'cedrum-clerk-profileNavbar',
  navbarButton: 'cedrum-clerk-profileNavbarButton',
  profileSection: 'cedrum-clerk-profileSection',
  profileSectionTitleText: 'cedrum-clerk-profileSectionTitle',
  profileSectionSubtitleText: 'cedrum-clerk-profileSectionSubtitle',
  profileSectionPrimaryButton: 'cedrum-clerk-profileSectionPrimaryButton',
  organizationProfilePage: 'cedrum-clerk-orgProfilePage',
  organizationProfileSection: 'cedrum-clerk-orgProfileSection',
  organizationProfileSectionTitleText: 'cedrum-clerk-orgProfileSectionTitle',
  organizationProfileSectionSubtitleText: 'cedrum-clerk-orgProfileSectionSubtitle',
  organizationProfileSectionPrimaryButton: 'cedrum-clerk-orgProfileSectionPrimaryButton',
});

type LocalizationNode = Record<string, unknown>;

const CEDRUM_CLERK_LOCALIZATION_EN = Object.freeze({
  locale: 'en-US',
  signIn: Object.freeze({
    start: Object.freeze({
      title: 'Welcome back',
      subtitle: 'Sign in to continue with cedrum.',
    }),
    password: Object.freeze({
      title: 'Enter your password',
      subtitle: 'Use your cedrum password to continue.',
    }),
    forgotPassword: Object.freeze({
      title: 'Reset your password',
      subtitle: 'We\'ll email you a secure reset code.',
    }),
  }),
  signUp: Object.freeze({
    start: Object.freeze({
      title: 'Create your cedrum account',
      subtitle: 'Set up your workspace and start shipping faster.',
    }),
    continue: Object.freeze({
      title: 'Complete your account',
      subtitle: 'One quick step left before you\'re in.',
    }),
    emailCode: Object.freeze({
      title: 'Verify your email',
      subtitle: 'Enter the code we sent to your inbox.',
    }),
  }),
});

const CEDRUM_CLERK_LOCALIZATION_DE = Object.freeze({
  locale: 'de-DE',
  signIn: Object.freeze({
    start: Object.freeze({
      title: 'Willkommen zurück',
      subtitle: 'Melde dich an, um mit cedrum fortzufahren.',
    }),
    password: Object.freeze({
      title: 'Passwort eingeben',
      subtitle: 'Verwende dein cedrum-Passwort, um fortzufahren.',
    }),
    forgotPassword: Object.freeze({
      title: 'Passwort zurücksetzen',
      subtitle: 'Wir senden dir einen sicheren Code zum Zurücksetzen.',
    }),
  }),
  signUp: Object.freeze({
    start: Object.freeze({
      title: 'cedrum-Konto erstellen',
      subtitle: 'Richte deinen Workspace ein und arbeite schneller.',
    }),
    continue: Object.freeze({
      title: 'Konto vervollständigen',
      subtitle: 'Noch ein kurzer Schritt, dann bist du drin.',
    }),
    emailCode: Object.freeze({
      title: 'E-Mail bestätigen',
      subtitle: 'Gib den Code ein, den wir an dein Postfach gesendet haben.',
    }),
  }),
});

const CEDRUM_CLERK_LOCALIZATION_ES = Object.freeze({
  locale: 'es-ES',
  unstable__errors: Object.freeze({
    zxcvbn: Object.freeze({
      couldBeStronger:
        'Tu contraseña funciona, pero podría ser más segura. Prueba a añadir más caracteres.',
      goodPassword: 'Tu contraseña cumple todos los requisitos.',
      notEnough: 'Tu contraseña no es lo suficientemente segura.',
      suggestions: Object.freeze({
        allUppercase: 'Usa mayúsculas en algunas letras, no en todas.',
        anotherWord: 'Añade más palabras menos comunes.',
        associatedYears: 'Evita años asociados a datos personales.',
        capitalization: 'No pongas solo la primera letra en mayúscula.',
        dates: 'Evita fechas asociadas a datos personales.',
        l33t: 'Evita sustituciones predecibles, como \'@\' por \'a\'.',
        longerKeyboardPattern:
          'Usa patrones de teclado más largos y cambia la dirección al escribir.',
        noNeed:
          'Puedes crear contraseñas seguras sin símbolos, números ni mayúsculas.',
        pwned: 'Si usas esta contraseña en otro sitio, deberías cambiarla.',
        recentYears: 'Evita años recientes.',
        repeated: 'Evita repetir palabras y caracteres.',
        reverseWords: 'Evita escribir palabras comunes al revés.',
        sequences: 'Evita secuencias de caracteres comunes.',
        useWords: 'Usa varias palabras, pero evita frases comunes.',
      }),
      warnings: Object.freeze({
        common: 'Esta contraseña se usa con frecuencia.',
        commonNames:
          'Los nombres y apellidos comunes son fáciles de adivinar.',
        dates: 'Las fechas son fáciles de adivinar.',
        extendedRepeat:
          'Los patrones repetidos como "abcabcabc" son fáciles de adivinar.',
        keyPattern:
          'Los patrones cortos de teclado son fáciles de adivinar.',
        namesByThemselves:
          'Los nombres o apellidos por sí solos son fáciles de adivinar.',
        pwned:
          'Esta contraseña apareció en una filtración de datos en internet.',
        recentYears: 'Los años recientes son fáciles de adivinar.',
        sequences:
          'Las secuencias comunes como "abc" son fáciles de adivinar.',
        similarToCommon:
          'Esta contraseña se parece a otras contraseñas comunes.',
        simpleRepeat:
          'Los caracteres repetidos como "aaa" son fáciles de adivinar.',
        straightRow:
          'Las líneas rectas de teclas del teclado son fáciles de adivinar.',
        topHundred: 'Esta contraseña se usa con mucha frecuencia.',
        topTen: 'Esta contraseña se usa muchísimo.',
        userInputs:
          'No incluyas datos personales ni relacionados con este sitio.',
        wordByItself: 'Las palabras sueltas son fáciles de adivinar.',
      }),
    }),
  }),
  signIn: Object.freeze({
    start: Object.freeze({
      title: 'Bienvenido de nuevo',
      subtitle: 'Inicia sesión para continuar con cedrum.',
    }),
    password: Object.freeze({
      title: 'Introduce tu contraseña',
      subtitle: 'Usa tu contraseña de cedrum para continuar.',
    }),
    forgotPassword: Object.freeze({
      title: 'Restablece tu contraseña',
      subtitle: 'Te enviaremos un código seguro para restablecerla.',
    }),
  }),
  signUp: Object.freeze({
    start: Object.freeze({
      title: 'Crea tu cuenta de cedrum',
      subtitle: 'Configura tu workspace y avanza más rápido.',
    }),
    continue: Object.freeze({
      title: 'Completa tu cuenta',
      subtitle: 'Solo queda un paso rápido para entrar.',
    }),
    emailCode: Object.freeze({
      title: 'Verifica tu email',
      subtitle: 'Introduce el código que enviamos a tu bandeja de entrada.',
    }),
  }),
});

const CEDRUM_CLERK_LOCALIZATION_FR = Object.freeze({
  locale: 'fr-FR',
  signIn: Object.freeze({
    start: Object.freeze({
      title: 'Bon retour',
      subtitle: 'Connectez-vous pour continuer avec cedrum.',
    }),
    password: Object.freeze({
      title: 'Saisissez votre mot de passe',
      subtitle: 'Utilisez votre mot de passe cedrum pour continuer.',
    }),
    forgotPassword: Object.freeze({
      title: 'Réinitialiser votre mot de passe',
      subtitle: 'Nous vous enverrons un code sécurisé de réinitialisation.',
    }),
  }),
  signUp: Object.freeze({
    start: Object.freeze({
      title: 'Créer votre compte cedrum',
      subtitle: 'Configurez votre workspace et avancez plus vite.',
    }),
    continue: Object.freeze({
      title: 'Compléter votre compte',
      subtitle: 'Encore une étape rapide avant de commencer.',
    }),
    emailCode: Object.freeze({
      title: 'Vérifiez votre email',
      subtitle: 'Saisissez le code envoyé dans votre boîte de réception.',
    }),
  }),
});

function mergeLocalization(
  base: LocalizationNode,
  overrides: LocalizationNode = {},
): LocalizationNode {
  const merged: LocalizationNode = { ...base };

  for (const [key, value] of Object.entries(overrides)) {
    if (typeof value === 'undefined') {
      continue;
    }

    const baseValue = merged[key];
    const shouldMerge =
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      baseValue &&
      typeof baseValue === 'object' &&
      !Array.isArray(baseValue);

    merged[key] = shouldMerge
      ? mergeLocalization(
          baseValue as LocalizationNode,
          value as LocalizationNode,
        )
      : value;
  }

  return merged;
}

export const CEDRUM_CLERK_LOCALIZATION = mergeLocalization(
  enUS as LocalizationNode,
  CEDRUM_CLERK_LOCALIZATION_EN as LocalizationNode,
);

export function getCedrumClerkLocalization(locale?: string) {
  const normalizedLocale = String(locale || '')
    .trim()
    .toLowerCase()
    .split(/[-_]/)[0];

  if (normalizedLocale === 'de') {
    return [
      deDE as LocalizationNode,
      CEDRUM_CLERK_LOCALIZATION_DE as LocalizationNode,
    ].reduce(
      (localization, overrides) => mergeLocalization(localization, overrides),
      CEDRUM_CLERK_LOCALIZATION as LocalizationNode,
    );
  }

  if (normalizedLocale === 'es') {
    return [
      esES as LocalizationNode,
      CEDRUM_CLERK_LOCALIZATION_ES as LocalizationNode,
    ].reduce(
      (localization, overrides) => mergeLocalization(localization, overrides),
      CEDRUM_CLERK_LOCALIZATION as LocalizationNode,
    );
  }

  if (normalizedLocale === 'fr') {
    return [
      frFR as LocalizationNode,
      CEDRUM_CLERK_LOCALIZATION_FR as LocalizationNode,
    ].reduce(
      (localization, overrides) => mergeLocalization(localization, overrides),
      CEDRUM_CLERK_LOCALIZATION as LocalizationNode,
    );
  }

  return CEDRUM_CLERK_LOCALIZATION;
}

function readCssVar(name: string, fallbackValue: string): string {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return fallbackValue;
  }

  const resolved = window.getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return resolved || fallbackValue;
}

function normalizeMode(mode?: ClerkMode): ClerkMode {
  return mode === 'dark' ? 'dark' : 'light';
}

function normalizeSurface(surface?: ClerkSurface): ClerkSurface {
  if (surface === 'page' || surface === 'modal' || surface === 'popover') {
    return surface;
  }

  return 'embedded';
}

function resolveBrandVariant(
  brandVariant: ClerkBrandVariant | undefined,
  surface: ClerkSurface,
): 'lockup' | 'mark' {
  if (brandVariant === 'lockup' || brandVariant === 'mark') {
    return brandVariant;
  }

  return surface === 'popover' ? 'mark' : 'lockup';
}

function getThemeFallback(mode: ClerkMode) {
  return mode === 'dark' ? THEME_FALLBACKS.dark : THEME_FALLBACKS.light;
}

function buildElements(surface: ClerkSurface) {
  return {
    ...BASE_ELEMENTS,
    rootBox: `cedrum-clerk cedrum-clerk-root cedrum-clerk-surface-${surface}`,
  };
}

export function getClerkAppearance(options: ClerkAppearanceOptions = {}) {
  const mode = normalizeMode(options.mode);
  const surface = normalizeSurface(options.surface);
  const variant = resolveBrandVariant(options.brandVariant, surface);
  const fallback = getThemeFallback(mode);

  return {
    theme: shadcn,
    layout: {
      logoPlacement: 'inside',
      logoImageUrl:
        mode === 'dark' ? CEDRUM_BRAND_LOGOS[variant].dark : CEDRUM_BRAND_LOGOS[variant].light,
      logoLinkUrl: options.logoLinkUrl || '/',
      socialButtonsVariant: 'blockButton',
      socialButtonsPlacement: 'bottom',
      animations: false,
      termsPageUrl: options.termsPageUrl || '/terms',
      privacyPageUrl: options.privacyPageUrl || '/privacy',
      helpPageUrl: options.helpPageUrl || '/#faq',
    },
    variables: {
      fontFamily: readCssVar(
        '--ui-font-family',
        '"Inter", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      ),
      fontFamilyButtons: readCssVar(
        '--ui-font-family',
        '"Inter", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      ),
      fontSize: readCssVar('--ui-font-size-md', '1rem'),
      spacing: readCssVar('--ui-space-4', '16px'),
      borderRadius: readCssVar('--ui-radius-md', '12px'),
      colorPrimary: readCssVar('--ui-accent', fallback.accent),
      colorPrimaryForeground: readCssVar('--ui-accent-contrast', fallback.accentContrast),
      colorDanger: fallback.danger,
      colorSuccess: fallback.success,
      colorWarning: fallback.warning,
      colorForeground: readCssVar('--ui-text-1', fallback.text),
      colorMutedForeground: readCssVar('--ui-text-2', fallback.textMuted),
      colorBackground: readCssVar('--ui-surface-1', fallback.surface1),
      colorInput: readCssVar('--ui-surface-2', fallback.surface2),
      colorInputForeground: readCssVar('--ui-text-1', fallback.text),
      colorBorder: readCssVar('--ui-border', fallback.border),
      colorNeutral: readCssVar('--ui-border-strong', fallback.borderStrong),
      colorRing: readCssVar('--ui-focus-ring', fallback.ring),
      colorModalBackdrop: fallback.modalBackdrop,
      colorShadow: fallback.shadowColor,
    },
    elements: buildElements(surface),
    captcha: {
      theme: mode,
    },
  };
}
